<template>
  <div class="product">
    <div class="image-wrapper">
      <img
        v-if="groupedProductImage"
        :src="groupedProductImage.smallMedia"
        alt="Product image"
      />
    </div>
    <div class="text">
      <div class="info">
        <a :href="product.onlineStoreUrl" class="product-link">
          <h3 class="title">
            {{ product.title }}
          </h3>
        </a>
        <p class="price">
          {{
            $filters.toCurrency(
              product.priceRange.minVariantPrice.amount,
              product.priceRange.minVariantPrice.currencyCode,
              0
            )
          }}
        </p>
      </div>
      <div class="input">
        <DropDown
          :options="dropdownVariants"
          default-label="Select size"
          theme="secondary"
          form="outline"
          :error="v$.selectedVariant.$errors.length > 0"
          @changed="handleVariantChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { unwrapNodes } from "@/helpers/graphql";
import { required, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import DropDown from "@/components/DropDown.vue";

export default {
  name: "SetProduct",
  components: {
    DropDown,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  emits: ["variant-selected"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      selectedVariant: "",
    };
  },
  computed: {
    dropdownVariants() {
      return this.variants.map((variant) => {
        return {
          value: variant.id,
          label: variant.title,
          disabled: !variant.availableForSale,
          status: this.quantityStatus(variant.quantityAvailable),
        };
      });
    },
    groupedProductImage() {
      const unwrappedImageNodes = unwrapNodes(this.product.images?.edges);
      const unwrappedFlatlayNodes = unwrapNodes(
        this.product.flatlayImages?.references.edges
      );

      const image = unwrappedFlatlayNodes
        ? unwrappedFlatlayNodes[0].image
        : unwrappedImageNodes
        ? unwrappedImageNodes[0]
        : null;

      return image;
    },
    variants() {
      return unwrapNodes(this.product.variants?.edges) || [];
    },
  },
  methods: {
    handleVariantChange(value) {
      this.selectedVariant = value;
      this.$emit("variant-selected", value);
    },
    quantityStatus(quantity) {
      if (quantity <= 0) {
        return "Out of stock";
      }
      if (quantity <= 15 && quantity > 5) {
        return "Low stock";
      }
      if (quantity <= 5) {
        return (
          quantity + " " + this.$filters.pluralize("item", quantity) + " left"
        );
      }
      return null;
    },
  },
  validations() {
    return {
      selectedVariant: {
        required: helpers.withMessage("Please select a variant", required),
      },
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form";

.product {
  display: flex;
  gap: 8px;
  padding: 10px 0;
  align-items: center;

  @include respond-above(md) {
    padding: 24px 0;
    gap: 16px;
    border-top: 1px solid $color-grey-light;
  }

  .text {
    flex: 1 0 auto;
    padding-top: 24px;

    @include respond-above(lg) {
      display: flex;
    }

    .info,
    .input {
      flex: 1 0 auto;
    }
  }
}

.image-wrapper {
  display: block;
  background: $color-grey-lightest;
  aspect-ratio: 4/5;
  width: 150px;

  @include respond-above(md) {
    width: 80px;
  }

  img {
    object-fit: contain;
    height: 100%;
    object-position: center center;
    display: block;
    width: 100%;
  }
}

.product-link {
  text-decoration: none;
}

.title {
  @include editorial-headline(16px);
  margin-bottom: 2px;
}

.price {
  @include utility-body(14px);
  margin-bottom: 16px;
}
</style>
